import React, { useState, useRef, useEffect } from "react";
import { Quote, PlayCircle, X } from "lucide-react";
import { caseStudies } from "@/data/caseStudies";

// NelsonTrilogyStrip — three signature campaigns built for Nelson County,
// shown as side-by-side mini-cards on /tourism/dmos. Clicking "See full
// report" swaps that card's looping muted thumbnail for a full-controls
// player with audio, INLINE inside the card's column — never a fullscreen
// modal. Other cards keep looping silently. A second card can be played
// at any time; the first one auto-reverts to its thumbnail.

const TRILOGY = [
  {
    slug: "nelson-county-tourism",
    short: "Wanderlove",
    headline: "Wanderlove",
    role: "12-month omni-channel brand campaign",
    accent: "from-emerald-400/20 to-sky-500/10",
  },
  {
    slug: "dark-skies",
    short: "Dark Skies",
    headline: "Dark Skies",
    role: "10-month experiential push · 823K+ impressions",
    accent: "from-indigo-500/25 to-purple-500/10",
  },
  {
    slug: "eat-drink-love",
    short: "Eat. Drink. Love. Trail",
    headline: "Eat. Drink. Love. Trail",
    role: "Gamified trail · Engagement +255% above benchmark",
    accent: "from-rose-500/25 to-amber-400/10",
  },
];

const TrilogyCard = ({
  cs,
  short,
  role,
  headline,
  accent,
  isPlaying,
  onPlay,
  onStop,
}) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!isPlaying) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    v.currentTime = 0;
    v.muted = false;
    v.play().catch(() => {
      // Fall back to muted autoplay if the browser blocks unmuted play.
      v.muted = true;
      v.play().catch(() => {});
    });
    return () => {
      try {
        v.pause();
      } catch {
        /* noop */
      }
    };
  }, [isPlaying]);

  return (
    <div
      className="surface overflow-hidden flex flex-col h-full group"
      data-testid={`trilogy-card-${cs.slug}`}
    >
      <div
        className={`relative aspect-video overflow-hidden bg-gradient-to-br ${accent}`}
      >
        {isPlaying ? (
          // Full-controls player, audio on, contained in the card column.
          // autoPlay + muted={false} works because this render is triggered
          // by the user clicking "See full report" (a user gesture), which
          // satisfies the browser's autoplay-with-audio policy.
          <>
            <video
              ref={videoRef}
              src={cs.videoUrl}
              poster={cs.videoPoster}
              autoPlay
              controls
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-contain bg-black"
              data-testid={`trilogy-report-${cs.slug}`}
            />
            <button
              type="button"
              onClick={onStop}
              aria-label="Close report"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-[var(--af-navy-deep)]/85 hover:bg-[var(--af-navy-deep)] text-[var(--af-cream)] flex items-center justify-center border border-[var(--af-cream)]/20 backdrop-blur"
              data-testid={`trilogy-close-${cs.slug}`}
            >
              <X size={16} />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onPlay}
            aria-label={`Play full ${short} report`}
            className="absolute inset-0 w-full h-full cursor-pointer"
          >
            {cs.videoUrl ? (
              <video
                src={cs.videoUrl}
                poster={cs.videoPoster}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                onLoadedMetadata={(e) => {
                  e.currentTarget.play().catch(() => {});
                }}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                data-testid={`trilogy-video-${cs.slug}`}
              />
            ) : (
              <img
                src={cs.image}
                alt={cs.client}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--af-navy-deep)] via-transparent to-transparent" />
            <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--af-navy-deep)]/80 backdrop-blur text-[10px] uppercase tracking-widest text-[var(--af-cream)]/85 border border-[var(--af-line)]/60">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
              {short}
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
              <PlayCircle
                size={56}
                className="text-[var(--af-lime)] drop-shadow-lg"
              />
            </div>
          </button>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-baseline gap-3 mb-2">
          <div className="font-display text-4xl sm:text-5xl text-[var(--af-lime)] leading-none">
            {cs.heroMetric.value}
          </div>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-[var(--af-cream)]/65 mb-4">
          {cs.heroMetric.label}
        </div>
        <h3 className="font-display text-2xl sm:text-3xl leading-tight mb-2">
          {headline || cs.headline}
        </h3>
        <div className="text-xs text-[var(--af-cream)]/60 mb-4">{role}</div>
        <div className="mt-auto pt-4 border-t border-[var(--af-line)]/60">
          {isPlaying ? (
            <button
              type="button"
              onClick={onStop}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--af-cream)]/80 hover:text-[var(--af-lime)]"
              data-testid={`trilogy-close-link-${cs.slug}`}
            >
              <X size={14} /> Close report
            </button>
          ) : (
            <button
              type="button"
              onClick={onPlay}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--af-lime)] hover:underline underline-offset-4"
              data-testid={`trilogy-see-report-${cs.slug}`}
            >
              <PlayCircle size={14} /> See full report
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const NelsonTrilogyStrip = () => {
  const [playingSlug, setPlayingSlug] = useState(null);
  const cards = TRILOGY.map((t) => ({ ...t, cs: caseStudies[t.slug] })).filter(
    (t) => t.cs,
  );
  if (cards.length === 0) return null;

  return (
    <section
      className="px-5 sm:px-8 py-20"
      data-testid="nelson-trilogy-strip"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 max-w-5xl flex items-start gap-6 sm:gap-10">
          <div className="flex-1 min-w-0 max-w-3xl">
            <div className="eyebrow mb-4">Featured Work · Nelson County, VA</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
              Three signature campaigns we built for{" "}
              <span className="text-[var(--af-lime)]">Nelson County.</span>
            </h2>
            <p className="text-base sm:text-lg text-[var(--af-cream)]/75 mt-5 leading-relaxed">
              One DMO. Three distinct campaign concepts, brand, experiential,
              and gamified, each engineered for a different season, audience,
              and grant cycle. The same agency, the same playbook, year after
              year.
            </p>
          </div>
          <img
            src="/media/clients/visit-nelson-county-vifl.png"
            alt="Visit Nelson County · Virginia is for Lovers"
            className="h-24 sm:h-32 w-auto opacity-90 flex-shrink-0 hidden sm:block mt-10"
            data-testid="trilogy-vnc-vifl-logo"
          />
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {cards.map(({ slug, cs, short, role, headline, accent }) => (
            <TrilogyCard
              key={slug}
              cs={cs}
              short={short}
              role={role}
              headline={headline}
              accent={accent}
              isPlaying={playingSlug === slug}
              onPlay={() => setPlayingSlug(slug)}
              onStop={() => setPlayingSlug(null)}
            />
          ))}
        </div>

        <figure
          className="mt-14 max-w-4xl mx-auto text-center px-4"
          data-testid="trilogy-maureen-quote"
        >
          <Quote
            size={32}
            className="text-[var(--af-lime)] mx-auto mb-5 opacity-80"
          />
          <blockquote className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight text-[var(--af-cream)]">
            “Anne Flinn Advertising's campaigns strengthened brand awareness,
            increased visitor engagement, improved marketing effectiveness,
            and{" "}
            <span className="text-[var(--af-lime)]">
              navigated changing tourism trends
            </span>{" "}
            with innovative and thoughtful campaigns.”
          </blockquote>
          <figcaption className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-cream)]/65 mt-6">
            Maureen Kelley · Director of Economic Development & Tourism,
            Nelson County, VA
          </figcaption>
        </figure>
      </div>
    </section>
  );
};

export default NelsonTrilogyStrip;
